import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../api/client';
import { useCan } from '../../../lib/rbac';
import {
  X, Search, Loader2, ShoppingCart, Check,
  AlertCircle, Package, Barcode, Type, Key,
} from 'lucide-react';

interface AmazonSearchPanelProps {
  sellerId: number;
  keyword?: string;
  itemId?: string;
  title?: string;
  onClose: () => void;
}

interface AmazonResult {
  asin: string;
  title: string;
  price?: number;
  currency?: string;
  image?: string;
  rating?: number;
  monthly_sold?: number;
  status: 'new' | 'in_queue' | 'published' | 'duplicate';
}

interface SearchResponse {
  results: AmazonResult[];
  search_method: 'gtin' | 'text';
  keepa_tokens_remaining?: number;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  new: { label: 'tendencias.statusNew', color: 'text-green-700 dark:text-green-300', bg: 'bg-green-100 dark:bg-green-900/30' },
  in_queue: { label: 'tendencias.statusInQueue', color: 'text-blue-700 dark:text-blue-300', bg: 'bg-blue-100 dark:bg-blue-900/30' },
  published: { label: 'tendencias.statusPublished', color: 'text-purple-700 dark:text-purple-300', bg: 'bg-purple-100 dark:bg-purple-900/30' },
  duplicate: { label: 'tendencias.statusDuplicate', color: 'text-gray-600 dark:text-gray-400', bg: 'bg-gray-100 dark:bg-gray-800/50' },
};

export default function AmazonSearchPanel({ sellerId, keyword, itemId, title, onClose }: AmazonSearchPanelProps) {
  const { t } = useTranslation();
  const canMoney = useCan()('global.view_money');
  const [results, setResults] = useState<AmazonResult[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [queueing, setQueueing] = useState(false);
  const [searchMethod, setSearchMethod] = useState<'gtin' | 'text' | null>(null);
  const [keepaTokens, setKeepaTokens] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [queueSuccess, setQueueSuccess] = useState<number | null>(null);

  const searchAmazon = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResults([]);
    setSelected(new Set());
    setQueueSuccess(null);
    try {
      const body: Record<string, unknown> = { seller_id: sellerId };
      if (keyword) body.keyword = keyword;
      if (itemId) body.item_id = itemId;

      const res = await api.post<SearchResponse>('/publisher/discover/tendencias/search-amazon', body);
      if (res.success && res.data) {
        const d = res.data as any;
        const items = d.items || d.results || [];
        setResults(items);
        setSearchMethod(d.search_method || '');
        if (d.tokens_left != null || d.keepa_tokens_remaining != null) {
          setKeepaTokens(d.tokens_left ?? d.keepa_tokens_remaining);
        }
        // Auto-select non-duplicates
        const autoSelect = new Set<string>();
        for (const r of items) {
          if (!r.is_duplicate && !r.already_published) {
            autoSelect.add(r.asin);
          }
        }
        setSelected(autoSelect);
      } else {
        setError(res.error === 'keepa_rate_limit' ? t('tendencias.keepaQuota') : (res.error || res.message || 'Search failed'));
      }
    } catch (err) {
      console.error('Amazon search failed', err);
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, [sellerId, keyword, itemId]);

  useEffect(() => {
    searchAmazon();
  }, [searchAmazon]);

  const toggleSelect = (asin: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(asin)) next.delete(asin);
      else next.add(asin);
      return next;
    });
  };

  const toggleAll = () => {
    const selectable = results.filter(r => r.status === 'new');
    if (selected.size === selectable.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(selectable.map(r => r.asin)));
    }
  };

  const handleQueue = async () => {
    if (selected.size === 0) return;
    setQueueing(true);
    setError(null);
    try {
      const res = await api.post<{ queued: number }>('/publisher/discover/tendencias/queue', {
        asins: Array.from(selected),
        seller_id: sellerId,
        sites: [], // Let backend use defaults
      });
      if (res.success && res.data) {
        setQueueSuccess((res.data as any)?.queued || (res.data as any)?.enqueued || selected.size);
        // Mark queued items as in_queue
        setResults(prev =>
          prev.map(r => selected.has(r.asin) ? { ...r, status: 'in_queue' as const } : r)
        );
        setSelected(new Set());
      } else {
        setError(res.error || 'Queue failed');
      }
    } catch (err) {
      console.error('Queue failed', err);
      setError('Network error');
    } finally {
      setQueueing(false);
    }
  };

  const selectableCount = results.filter(r => r.status === 'new').length;

  return (
    <div
      className="rounded-lg border flex flex-col"
      style={{
        borderColor: 'var(--color-border)',
        backgroundColor: 'var(--color-bg-secondary)',
        maxHeight: '700px',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--color-border)' }}>
        <div className="flex items-center gap-2 min-w-0">
          <Search className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-primary)' }} />
          <h3 className="text-sm font-semibold truncate" style={{ color: 'var(--color-text)' }}>
            {t('tendencias.amazonSearch')}
          </h3>
          {/* Search context */}
          {(keyword || title) && (
            <span
              className="text-xs truncate max-w-[200px]"
              style={{ color: 'var(--color-text-tertiary)' }}
              title={keyword || title}
            >
              — {keyword || title}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded-md hover:opacity-70 transition-opacity flex-shrink-0"
          style={{ color: 'var(--color-text-tertiary)' }}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Method indicator + Keepa tokens */}
      {(searchMethod || keepaTokens != null) && (
        <div
          className="flex items-center justify-between px-4 py-1.5 text-xs border-b"
          style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-tertiary)' }}
        >
          {searchMethod && (
            <span className="flex items-center gap-1">
              {searchMethod === 'gtin' ? (
                <><Barcode className="w-3 h-3" /> GTIN</>
              ) : (
                <><Type className="w-3 h-3" /> {t('tendencias.textSearch')}</>
              )}
            </span>
          )}
          {keepaTokens != null && (
            <span className="flex items-center gap-1">
              <Key className="w-3 h-3" />
              Keepa: {keepaTokens.toLocaleString()} tokens
            </span>
          )}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--color-primary)' }} />
            <p className="text-sm" style={{ color: 'var(--color-text-tertiary)' }}>
              {t('tendencias.searchingAmazon')}
            </p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2">
            <AlertCircle className="w-6 h-6 text-red-500" />
            <p className="text-sm text-red-500">{error}</p>
          </div>
        ) : results.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2">
            <Package className="w-6 h-6" style={{ color: 'var(--color-text-tertiary)' }} />
            <p className="text-sm" style={{ color: 'var(--color-text-tertiary)' }}>
              {t('tendencias.noResults')}
            </p>
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b" style={{ borderColor: 'var(--color-border)' }}>
                <th className="px-3 py-2 text-left w-8">
                  <input
                    type="checkbox"
                    checked={selected.size === selectableCount && selectableCount > 0}
                    onChange={toggleAll}
                    className="rounded"
                  />
                </th>
                <th className="px-2 py-2 text-left w-12" />
                <th
                  className="px-2 py-2 text-left"
                  style={{ color: 'var(--color-text-tertiary)' }}
                >
                  {t('tendencias.colProduct')}
                </th>
                <th
                  className="px-2 py-2 text-right w-20"
                  style={{ color: 'var(--color-text-tertiary)' }}
                >
                  {t('tendencias.colPrice')}
                </th>
                <th
                  className="px-2 py-2 text-center w-16"
                  style={{ color: 'var(--color-text-tertiary)' }}
                >
                  {t('tendencias.colRating')}
                </th>
                <th
                  className="px-2 py-2 text-right w-20"
                  style={{ color: 'var(--color-text-tertiary)' }}
                >
                  {t('tendencias.colMonthlySold')}
                </th>
                <th
                  className="px-2 py-2 text-center w-24"
                  style={{ color: 'var(--color-text-tertiary)' }}
                >
                  {t('tendencias.colStatus')}
                </th>
              </tr>
            </thead>
            <tbody>
              {results.map(item => {
                const statusCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.new;
                const isSelectable = item.status === 'new';
                return (
                  <tr
                    key={item.asin}
                    className="border-b hover:opacity-90 transition-opacity"
                    style={{ borderColor: 'var(--color-border)' }}
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selected.has(item.asin)}
                        onChange={() => toggleSelect(item.asin)}
                        disabled={!isSelectable}
                        className="rounded disabled:opacity-30"
                      />
                    </td>
                    <td className="px-2 py-2">
                      {item.image ? (
                        <img src={item.image} alt="" className="w-10 h-10 object-contain rounded" loading="lazy" />
                      ) : (
                        <div
                          className="w-10 h-10 rounded flex items-center justify-center"
                          style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
                        >
                          <ShoppingCart className="w-4 h-4" style={{ color: 'var(--color-text-tertiary)' }} />
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      <div className="max-w-[250px]">
                        <p
                          className="font-medium truncate"
                          style={{ color: 'var(--color-text)' }}
                          title={item.title}
                        >
                          {item.title}
                        </p>
                        <p className="text-[10px] mt-0.5" style={{ color: 'var(--color-text-tertiary)' }}>
                          {item.asin}
                        </p>
                      </div>
                    </td>
                    <td className="px-2 py-2 text-right" style={{ color: 'var(--color-text)' }}>
                      {canMoney && item.price != null ? (
                        `$${item.price.toFixed(2)}`
                      ) : (
                        <span style={{ color: 'var(--color-text-tertiary)' }}>-</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center" style={{ color: 'var(--color-text)' }}>
                      {item.rating != null ? item.rating.toFixed(1) : '-'}
                    </td>
                    <td className="px-2 py-2 text-right" style={{ color: 'var(--color-text)' }}>
                      {item.monthly_sold != null ? item.monthly_sold.toLocaleString() : '-'}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${statusCfg.bg} ${statusCfg.color}`}>
                        {t(statusCfg.label)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer — Queue button */}
      {results.length > 0 && (
        <div
          className="flex items-center justify-between px-4 py-3 border-t"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <div className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
            {selected.size > 0
              ? `${selected.size} ${t('tendencias.selected')}`
              : t('tendencias.selectToQueue')
            }
          </div>
          <div className="flex items-center gap-2">
            {queueSuccess != null && (
              <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                <Check className="w-3.5 h-3.5" />
                {queueSuccess} {t('tendencias.queued')}
              </span>
            )}
            <button
              onClick={handleQueue}
              disabled={selected.size === 0 || queueing}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-medium text-white
                disabled:opacity-40 transition-opacity"
              style={{ backgroundColor: 'var(--color-primary)' }}
            >
              {queueing ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <ShoppingCart className="w-3.5 h-3.5" />
              )}
              {t('tendencias.enqueue')} ({selected.size})
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
